/**
 * Track-local tutorial API — these endpoints are NOT in the shared @/api/Api.ts
 * (which is read-only for this track), so they live here. They wrap the same
 * exported `axiosInstance` + `AuthError` so auth/redirect behavior is identical
 * to the rest of the app.
 *
 * Mirrors client-native/src/api/Api.ts tutorial endpoints (routes.go:149-156):
 *   GET  /auth/tutorial/progress
 *   POST /auth/tutorial/complete   { itemId }
 *   POST /auth/tutorial/dismiss
 *   POST /auth/tutorial/undismiss
 *   GET  /auth/tutorial/demoword
 *   GET  /auth/tutorial/demophrase
 */

import { AxiosError } from "axios";
import { axiosInstance, AuthError } from "@/api/Api";
import { tokenStorage } from "@/api/tokenStorage";
import {
  GetTutorialProgressResponse,
  PostCompleteTutorialItemRequest,
  GenericMessageResponse,
  GetDemoWordResponse,
  GetDemoPhraseResponse,
  TutorialItemID,
} from "@shared_types";

function requireToken(): string {
  const token = tokenStorage.getToken();
  if (!token) {
    throw new AuthError("Not authenticated");
  }
  return token;
}

function authHeaders(token: string) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

/** Generic axios-error normalizer matching @/api/Api.ts behavior. */
function rethrow(error: unknown): never {
  const axiosError = error as AxiosError;
  const status = axiosError.response?.status;
  let message = axiosError.message;
  const data = axiosError.response?.data as { message?: string } | undefined;
  if (data?.message) message = data.message;
  console.error(`[tutorialApi] ${message} (status ${status ?? "?"})`);
  throw new Error(message);
}

export async function getTutorialProgress(): Promise<GetTutorialProgressResponse> {
  const token = requireToken();
  try {
    const res = await axiosInstance.get<GetTutorialProgressResponse>(
      "/auth/tutorial/progress",
      authHeaders(token)
    );
    return res.data;
  } catch (error) {
    rethrow(error);
  }
}

export async function completeTutorialItem(
  itemId: TutorialItemID
): Promise<GenericMessageResponse> {
  const token = requireToken();
  const body: PostCompleteTutorialItemRequest = { itemId };
  try {
    const res = await axiosInstance.post<GenericMessageResponse>(
      "/auth/tutorial/complete",
      body,
      authHeaders(token)
    );
    return res.data;
  } catch (error) {
    rethrow(error);
  }
}

export async function dismissTutorial(): Promise<GenericMessageResponse> {
  const token = requireToken();
  try {
    const res = await axiosInstance.post<GenericMessageResponse>(
      "/auth/tutorial/dismiss",
      {},
      authHeaders(token)
    );
    return res.data;
  } catch (error) {
    rethrow(error);
  }
}

export async function undismissTutorial(): Promise<GenericMessageResponse> {
  const token = requireToken();
  try {
    const res = await axiosInstance.post<GenericMessageResponse>(
      "/auth/tutorial/undismiss",
      {},
      authHeaders(token)
    );
    return res.data;
  } catch (error) {
    rethrow(error);
  }
}

export async function getDemoWord(): Promise<GetDemoWordResponse> {
  const token = requireToken();
  try {
    const res = await axiosInstance.get<GetDemoWordResponse>(
      "/auth/tutorial/demoword",
      authHeaders(token)
    );
    return res.data;
  } catch (error) {
    rethrow(error);
  }
}

export async function getDemoPhrase(): Promise<GetDemoPhraseResponse> {
  const token = requireToken();
  try {
    const res = await axiosInstance.get<GetDemoPhraseResponse>(
      "/auth/tutorial/demophrase",
      authHeaders(token)
    );
    return res.data;
  } catch (error) {
    rethrow(error);
  }
}
